/**
 * FormFieldContext.jsx
 * 
 * Context provider for form field rendering.
 * Provides access to all data dependencies without prop drilling.
 * 
 * Usage:
 * 1. Wrap your form rendering area with <FormFieldProvider>
 * 2. Use useFormFieldContext() hook in any field component
 */

import React, { createContext, useContext, useMemo } from 'react';

// Create the context
const FormFieldContext = createContext(null);

/**
 * All dependencies needed by form fields
 * This mirrors what's available in App.js renderField scope
 */
const defaultContextValue = {
  // === CLIENT DATA ===
  selectedClient: null,           // Currently selected client object
  clients: [],                    // All clients array
  selectedNoteClients: [],        // Array of client IDs for current note
  clientDiagnoses: [],            // All client diagnoses
  clientVitals: [],               // Client vitals for current client
  
  // === USER DATA ===
  user: null,                     // Current logged-in user
  users: [],                      // All users array
  
  // === APPOINTMENT DATA ===
  appointments: [],               // All appointments
  
  // === FORM DATA ===
  newNoteFormData: {},            // Current form values during note creation
  formTemplates: [],              // All form templates
  
  // === TREATMENT PLAN DATA ===
  linkedGoals: [],                // Goals linked to current note
  treatmentPlanGoals: [],         // Available goals for selection
  
  // === TELEHEALTH/CONSENT DATA ===
  telehealthConsent: null,        // Telehealth consent data
  setTelehealthConsent: () => {}, // Callback to update consent
  organizations: [],              // Organizations with POS codes
  posCodes: [],                   // Fallback POS codes
  
  // === DROPDOWN DATA ===
  serviceTypes: [],               // Service types for dropdowns
  locations: [],                  // Locations for dropdowns
  providers: [],                  // Providers for dropdowns (filtered users)
  allGroups: [],                  // Groups for dropdowns
  
  // === GROUP NOTE DATA ===
  isGroupNote: false,             // Whether current note is a group note
  groupMembers: [],               // Members of current group
  groupAttendance: {},            // Attendance tracking for group
  groupMemberNotes: {},           // Per-member notes for group
  
  // === NOTE STATE ===
  selectedNote: null,             // Currently selected note (for edit mode)
  isEditMode: false,              // Whether we're editing vs creating
  
  // === RENDER MODE ===
  mode: 'edit',                   // 'edit' (interactive inputs) or 'view' (styled read-only)
  
  // === UI STATE ===
  disabled: false,                // Whether fields should be disabled
  
  // === CALLBACKS (will be null in visual-only mode) ===
  // These are set to no-op functions for visual testing
  setNewNoteFormData: () => {},
  setSelectedClient: () => {},
  setShowDiagnosisForm: () => {},
  setShowMedicationForm: () => {},
  setEditingMedication: () => {},
  setShowAddVitalsDialog: () => {},
  setShowLinkGoalsDialog: () => {},
  fetchTreatmentPlanGoals: () => {},
  setLinkedGoals: () => {},
  setGroupAttendance: () => {},
  setGroupMemberNotes: () => {},
  
  // === SIGNATURE DATA ===
  signatureData: {},
  setSignatureData: () => {},
};

/**
 * Provider component
 */
export const FormFieldProvider = ({ children, value = {} }) => {
  // Merge provided values with defaults
  const contextValue = useMemo(() => ({
    ...defaultContextValue,
    ...value,
  }), [value]);
  
  return (
    <FormFieldContext.Provider value={contextValue}>
      {children}
    </FormFieldContext.Provider>
  );
};

/**
 * Hook to access form field context
 */
export const useFormFieldContext = () => {
  const context = useContext(FormFieldContext);
  if (!context) {
    throw new Error('useFormFieldContext must be used within a FormFieldProvider');
  }
  return context;
};

/**
 * Create mock context data for sandbox/testing
 * This provides realistic-looking data for visual testing
 */
export const createMockContextData = () => ({
  selectedClient: {
    id: 'mock-client-1',
    full_name: 'John Doe',
    dob: '1985-06-15',
    date_of_birth: '1985-06-15',
    email: 'john.doe@example.com',
    phone: '555-123-4567',
    address: '123 Main St',
    city: 'Boston',
    state: 'MA',
    zipcode: '02101',
    intake_completed_at: '2024-01-15T10:00:00Z',
    created_at: '2024-01-10T09:00:00Z',
  },
  clients: [
    { id: 'mock-client-1', full_name: 'John Doe' },
    { id: 'mock-client-2', full_name: 'Jane Smith' },
  ],
  selectedNoteClients: ['mock-client-1'],
  clientDiagnoses: [
    {
      id: 'diag-1',
      client_id: 'mock-client-1',
      code: 'F32.1',
      description: 'Major depressive disorder, single episode, moderate',
      start_date: '2024-01-15',
      is_active: true,
      is_primary: true,
      provider_names: ['Dr. Sarah Johnson'],
    },
    {
      id: 'diag-2',
      client_id: 'mock-client-1',
      code: 'F41.1',
      description: 'Generalized anxiety disorder',
      start_date: '2024-02-01',
      is_active: true,
      is_primary: false,
      provider_names: ['Dr. Sarah Johnson'],
    },
  ],
  user: {
    id: 'mock-user-1',
    full_name: 'Dr. Sarah Johnson',
    email: 'sarah.johnson@clinic.com',
    role: 'Provider',
  },
  users: [
    { id: 'mock-user-1', full_name: 'Dr. Sarah Johnson' },
    { id: 'mock-user-2', full_name: 'Dr. Michael Chen' },
  ],
  appointments: [
    {
      id: 'appt-1',
      client_id: 'mock-client-1',
      start_time: '2024-12-01T14:00:00Z',
      status: 'completed',
    },
    {
      id: 'appt-2',
      client_id: 'mock-client-1',
      start_time: '2025-01-15T14:00:00Z',
      status: 'scheduled',
    },
  ],
  newNoteFormData: {},
  formTemplates: [],
  linkedGoals: [
    {
      goal_id: 'goal-1',
      pan_name: 'Improve Daily Functioning',
      goal_text: 'Client will develop and implement coping strategies for managing anxiety symptoms.',
      goal_in_clients_words: 'I want to feel less overwhelmed at work.',
      provider_name: 'Dr. Sarah Johnson',
      service_type: 'Individual Therapy',
      objectives: [
        { objective_id: 'obj-1', objective_text: 'Identify anxiety triggers', intervention: 'CBT techniques' },
        { objective_id: 'obj-2', objective_text: 'Practice breathing exercises daily', intervention: 'Mindfulness training' },
      ],
    },
  ],
  treatmentPlanGoals: [],
  telehealthConsent: {
    provider_location: 'loc-1',
    pos_code: '11',
    pos_description: 'Office',
    modality: 'in_person',
  },
  organizations: [
    {
      id: 'org-1',
      name: 'Demo Clinic',
      pos_codes: [
        { code: '02', description: 'Telehealth' },
        { code: '10', description: 'Telehealth in Patient Home' },
        { code: '11', description: 'Office' },
        { code: '12', description: 'Home' },
        { code: '53', description: 'Community Mental Health Center' },
      ]
    }
  ],
  posCodes: [
    { code: '02', description: 'Telehealth' },
    { code: '10', description: 'Telehealth in Patient Home' },
    { code: '11', description: 'Office' },
    { code: '12', description: 'Home' },
    { code: '53', description: 'Community Mental Health Center' },
  ],
  serviceTypes: [
    { id: 'st-1', name: 'Individual Therapy', display_name: 'Individual Therapy (IT)', is_active: true },
    { id: 'st-2', name: 'Group Therapy', display_name: 'Group Therapy (GT)', is_active: true },
    { id: 'st-3', name: 'Family Therapy', display_name: 'Family Therapy (FT)', is_active: true },
    { id: 'st-4', name: 'Anger Management', display_name: 'Anger Management (AM)', is_active: true },
    { id: 'st-5', name: 'Community Support Program', display_name: 'Community Support Program (CSP)', is_active: true },
    { id: 'st-6', name: 'Dual Diagnosis', display_name: 'Dual Diagnosis (DD)', is_active: true },
    { id: 'st-7', name: 'Medication Management', display_name: 'Medication Management (MM)', is_active: true },
    { id: 'st-8', name: 'Crisis Intervention', display_name: 'Crisis Intervention (CI)', is_active: true },
  ],
  locations: [
    { id: 'loc-1', name: 'Main Office' },
    { id: 'loc-2', name: 'Downtown Branch' },
  ],
  providers: [
    { id: 'mock-user-1', full_name: 'Dr. Sarah Johnson' },
    { id: 'mock-user-2', full_name: 'Dr. Michael Chen' },
  ],
  allGroups: [
    { id: 'group-1', name: 'Anxiety Support Group' },
    { id: 'group-2', name: 'Depression Recovery Group' },
  ],
  isGroupNote: false,
  groupMembers: [],
  groupAttendance: {},
  groupMemberNotes: {},
  selectedNote: null,
  isEditMode: false,
  disabled: false,
});

export default FormFieldContext;
